import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Save, X, Calendar, Hash, Building2, FileText, Scale, Gem, DollarSign, Award, CheckCircle, Users, TrendingUp, Calculator } from 'lucide-react';
import usePermissions from '../../hooks/usePermissions';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import JewelryCodeService from '../../services/JewelryCodeService';
import apiService from '../../services/apiService';
import { getGoldPurityFactor, stonesTotalGrams } from '../../utils/calculations';
import { calculateGoldNetWeight } from '../../shared/utils/jewelryCalculations';
import { todayCentral } from '../../shared/utils/dateUtils';

const AddInventory = () => {
  const { hasPermission, getPermissionLevel } = usePermissions();

  // Permission checks for add-inventory page
  const canCreate = hasPermission('add-inventory', 'create');
  // const canView = hasPermission('add-inventory', 'view'); // Not used, route protection handles this
  const permissionLevel = getPermissionLevel('add-inventory');

  // State management for component data
  const [jewelryPieces, setJewelryPieces] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [labelItemId, setLabelItemId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [currentGoldRate, setCurrentGoldRate] = useState(10000); // Default rate from dpgold.com

  const loadData = useCallback(async () => {
    try {
      // Load jewelry pieces from database
      await loadJewelryPieces();
      await loadMaterials();
      await loadCategories();
      await loadVendors();
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, []);

  // Load data from database
  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadJewelryPieces = async () => {
    try {
      const response = await fetch('/api/jewelry', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        console.log('=== LOADING JEWELRY PIECES ===');
        console.log('API Response:', data);
        console.log('Is Array:', Array.isArray(data));

        // Transform API data to match the display format
        const transformedData = Array.isArray(data) ? data.map(item => ({
          id: item.id,
          code: item.code,
          name: item.name,
          category: item.category_name || item.category,
          grossWeight: parseFloat(item.gross_weight) || 0,
          netWeight: parseFloat(item.net_weight) || 0,
          purity: item.gold_purity,
          totalGoldPrice: parseFloat(item.total_gold_price) || 0,
          totalStoneCost: parseFloat(item.total_stone_cost) || 0,
          totalCostValue: parseFloat(item.total_cost_value) || 0,
          stones: item.stones || [],
          status: item.status,
          createdDate: item.created_at || new Date().toISOString()
        })) : [];

        console.log('Transformed data:', transformedData.slice(0, 3)); // Show first 3 items
        setJewelryPieces(transformedData);
      }
    } catch (error) {
      console.error('Error loading jewelry pieces:', error);
      setJewelryPieces([]);
    }
  };
  const loadMaterials = async () => {
    try {
      const response = await fetch('/api/materials', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Materials loaded:', data);
        console.log('Total materials available:', data.length);
        console.log('Materials by category:', data.reduce((acc, m) => {
          acc[m.category] = (acc[m.category] || 0) + 1;
          return acc;
        }, {}));
        setMaterials(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading materials:', error);
      setMaterials([]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch('/api/categories', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          // Filter only child categories (the ones with codes for jewelry ID generation)
          const childCategories = data.filter(cat =>
            cat.type === 'child' &&
            cat.is_active === true &&
            cat.code &&
            cat.code.trim() !== ''
          );
          setCategories(childCategories);
        }
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await fetch('/api/vendors', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVendors(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
      setVendors([]);
    }
  };

  const loadCurrentGoldRate = async () => {
    try {
      const response = await fetch('/api/rates/gold/today', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.rate) {
          const goldRatePerGram = data.rate.gold_24k; // INR per gram
          setCurrentGoldRate(goldRatePerGram);
        }
      }
    } catch (error) {
      console.error('Error loading gold rate:', error);
    }
  };


  // Form state based on your requirements
  const [inventoryData, setInventoryData] = useState({
    // Section 1
    date: todayCentral(), // Current date but editable
    itemCode: {
      category: '',
      number: ''
    },

    // Section 2
    vendorId: '', // Use vendor ID for proper database relationship
    jewelryName: '', // Display name used in estimates & billing
    description: '',
    grossWeight: 0,
    netWeight: 0,
    finePercent: '', // Fine % (Kundan only) — vendor-supplied assay percentage
    kundanWeight: '', // Kundan gold weight (grams) — Kundan items only
    kundanPct: '',    // Kundan fine percentage — Kundan items only
    goldRate: 16565, // 24K gold rate per gram (loaded from gold_rates table on mount)
    pcs: 1,
    purity: '22KT', // Gold purity
    certificate: 'No',
    certificateRate: 750, // Default certificate rate per carat (₹750/carat)
    totalGoldPrice: 0,

    // Additional fields for future expansion
    makingCharges: 0,
    laborCharges: 0, // NEW: Separate labor charges field
    wastagePercentage: 0,
    otherCosts: 0,

    // Stone Management (NEW)
    stones: [], // Array of stone objects
    totalStoneCost: 0, // Total cost of all stones
    totalStoneWeight: 0 // Total weight of all stones in grams
  });

  // Category typeahead
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categoryHighlight, setCategoryHighlight] = useState(-1);

  // Stone typeahead: track query text and dropdown open state per stone row
  const [stoneSearch, setStoneSearch] = useState({}); // { [stoneId]: searchText }
  const [stoneDropdownOpen, setStoneDropdownOpen] = useState({}); // { [stoneId]: bool }
  const [stoneHighlight, setStoneHighlight] = useState({}); // { [stoneId]: index }
  const stoneInputRefs = useRef({}); // { [stoneId]: inputRef }
  const lastAddedStoneId = useRef(null);
  const [showCostPrice, setShowCostPrice] = useState(false);

  // Show cost price while backtick is held, hide on release
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === '`' && !e.repeat) setShowCostPrice(true); };
    const handleKeyUp = (e) => { if (e.key === '`') setShowCostPrice(false); };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Purity options with their numeric values for calculation
  const purityOptions = [
    { label: '14KT', value: '14KT', numeric: 14 },
    { label: '18KT', value: '18KT', numeric: 18 },
    { label: '22KT', value: '22KT', numeric: 22 },
    { label: '24KT', value: '24KT', numeric: 24 }
  ];

  // Auto-focus new stone row when added
  useEffect(() => {
    if (lastAddedStoneId.current && stoneInputRefs.current[lastAddedStoneId.current]) {
      stoneInputRefs.current[lastAddedStoneId.current].focus();
      lastAddedStoneId.current = null;
    }
  }, [inventoryData.stones]);

  // Load data on component mount
  useEffect(() => {
    loadData();
    loadCategories();
    loadVendors();
    loadCurrentGoldRate();

    // Set default sample data only if no data is available after 3 seconds
    const fallbackTimer = setTimeout(() => {
      if (categories.length === 0 && !categoriesLoading) {
        console.log('No categories loaded from API, using default categories');
        const defaultCategories = [
          { id: 1, name: 'Necklace', code: 'N', description: 'All types of necklaces' },
          { id: 2, name: 'Ring', code: 'R', description: 'All types of rings' },
          { id: 3, name: 'Earrings', code: 'E', description: 'All types of earrings' },
          { id: 4, name: 'Bracelet', code: 'B', description: 'All types of bracelets' },
          { id: 5, name: 'Pendant', code: 'P', description: 'All types of pendants' }
        ];
        setCategories(defaultCategories);
      }

      if (vendors.length === 0) {
        console.log('No vendors loaded from API, using default vendors');
        const defaultVendors = [
          { id: 1, name: 'Sample Vendor', company: 'Sample Vendor Co.', city: 'Sample City' }
        ];
        setVendors(defaultVendors);
      }
    }, 3000); // Wait 3 seconds for API calls to complete

    return () => clearTimeout(fallbackTimer);
  }, [categories.length, categoriesLoading, loadData, vendors.length]);

  // Form state based on your requirements

  // Load vendors


  // Generate enhanced jewelry ID using Categories table integration
  const generateItemNumber = async (categoryCode) => {
    try {
      // Find the category by code to get the ID
      const category = categories.find(cat => cat.code === categoryCode);
      if (!category) {
        console.error('Category not found for code:', categoryCode);
        console.log('Available categories:', categories);
        return `${categoryCode}-001`; // Fallback
      }

      console.log('Generating jewelry code for category:', category);

      // Use enhanced jewelry ID generator
      const jewelryCode = await JewelryCodeService.getNextCodeByCategoryId(category.id, 'dash');
      console.log('Generated jewelry code:', jewelryCode);
      return jewelryCode;
    } catch (error) {
      console.error('Error generating enhanced jewelry code:', error);
      console.log('Falling back to basic generation');

      // Fallback to basic generation if enhanced service fails
      const now = new Date();
      const year = now.getFullYear().toString().slice(-2);
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      const existingItems = (jewelryPieces || []).filter(item =>
        item.code && item.code.startsWith(`${categoryCode}-${year}${month}`)
      );
      const sequence = (existingItems.length + 1).toString().padStart(3, '0');
      const fallbackCode = `${categoryCode}-${year}${month}${sequence}`;
      console.log('Using fallback code:', fallbackCode);
      return fallbackCode;
    }
  };

  // Handle category selection and auto-generate item number
  const handleCategoryChange = async (categoryCode) => {
    try {
      // Show loading state
      setInventoryData(prev => ({
        ...prev,
        itemCode: {
          category: categoryCode,
          number: 'Generating...'
        }
      }));

      // Auto-populate purchase wastage & making from category defaults
      const cat = categories.find(c => c.code === categoryCode);
      if (cat) {
        const allCats = categories; // includes parents
        const parent = cat.parent_id ? allCats.find(c => c.id === cat.parent_id) : null;
        const purchaseWastage = parseFloat(cat.purchase_wastage_pct) > 0
          ? parseFloat(cat.purchase_wastage_pct)
          : (parent ? parseFloat(parent.purchase_wastage_pct) || 0 : 0);
        const purchaseMaking = parseFloat(cat.purchase_making_per_g) > 0
          ? parseFloat(cat.purchase_making_per_g)
          : (parent ? parseFloat(parent.purchase_making_per_g) || 0 : 0);
        setInventoryData(prev => ({
          ...prev,
          wastagePercentage: purchaseWastage,
          makingCharges: purchaseMaking
        }));
      }

      // Generate enhanced jewelry ID
      const itemNumber = await generateItemNumber(categoryCode);
      setInventoryData(prev => ({
        ...prev,
        itemCode: {
          category: categoryCode,
          number: itemNumber
        }
      }));
    } catch (error) {
      console.error('Error in category change:', error);
      // Fallback to show error state
      setInventoryData(prev => ({
        ...prev,
        itemCode: {
          category: categoryCode,
          number: 'Error generating code'
        }
      }));
    }
  };

  // Calculate total gold price using the formula: Net Weight × Gold Rate × purity factor
  const calculateTotalGoldPrice = (netWeight, goldRate, purity) => {
    const purityData = purityOptions.find(p => p.value === purity);
    if (!purityData) return 0;

    const purityFactor = getGoldPurityFactor(purityData.numeric);
    return netWeight * goldRate * purityFactor;
  };

  /**
   * Single source of truth for the pre-tax total cost breakdown.
   * Accepts an inventoryData-shaped object so it can be called from
   * both the save handler (with fresh local vars) and the live display
   * (with inventoryData directly).
   *
   * Returns { goldCost, wastageCost, stoneCost, makingCharges,
   *           laborCost, certificateCost, otherCosts, total }
   */
  const calculateCostBreakdown = (data) => {
    const netWeight = parseFloat(data.netWeight) || 0;
    const goldCost = parseFloat(data.totalGoldPrice) || 0;
    const wastageCost = goldCost * ((parseFloat(data.wastagePercentage) || 0) / 100);
    const stoneCost = parseFloat(data.totalStoneCost) || 0;
    const makingCharges = parseFloat(data.makingCharges) || 0;
    const laborCost = (parseFloat(data.laborCharges) || 0) * netWeight;
    const otherCosts = parseFloat(data.otherCosts) || 0;

    // Certificate cost: per diamond carat, unit-aware.
    // Diamond codes (e.g. "RD+2") don't contain the word "diamond" in their
    // name, so match against the materials table's category first — falling
    // back to the name check only when a stone has no materials-table match.
    const totalDiamondCarats = data.certificate === 'Yes'
      ? (data.stones || [])
        .filter(s => {
          const material = materials.find(m => m.code === s.stoneCode);
          return material ? material.category === 'Diamond'
            : !!(s.stoneName && s.stoneName.toLowerCase().includes('diamond'));
        })
        .reduce((sum, s) => {
          const w = parseFloat(s.weight) || 0;
          if (s.unit === 'gram') return sum + w * 5;   // 1g = 5 carats
          if (s.unit === 'number') return sum;
          return sum + w;                               // already carats
        }, 0)
      : 0;
    const certificateCost = (parseFloat(data.certificateRate) || 0) * totalDiamondCarats;

    const total = goldCost + wastageCost + stoneCost + makingCharges + laborCost + otherCosts + certificateCost;

    return { goldCost, wastageCost, stoneCost, makingCharges, laborCost, certificateCost, totalDiamondCarats, otherCosts, total };
  };

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setInventoryData(prev => {
      const newData = { ...prev, [field]: value };

      // Always derive stone weight from the stones array — never trust stale totalStoneWeight
      const stoneWeightGrams = stonesTotalGrams(prev.stones);

      // Recalculate dependent fields
      if (field === 'grossWeight') {
        newData.netWeight = calculateGoldNetWeight(value, stoneWeightGrams, newData.kundanWeight);
        newData.totalStoneWeight = stoneWeightGrams; // keep in sync
      }
      // Net Weight excludes the Kundan (Fine Net Wt = gross − stones keeps it)
      if (field === 'kundanWeight') {
        newData.netWeight = calculateGoldNetWeight(prev.grossWeight, stoneWeightGrams, value);
      }

      // Recalculate total gold price when relevant fields change
      if (field === 'grossWeight' || field === 'kundanWeight' || field === 'goldRate' || field === 'purity' || field === 'wastagePercentage') {
        const netWeight = (field === 'grossWeight' || field === 'kundanWeight')
          ? newData.netWeight
          : calculateGoldNetWeight(prev.grossWeight, stoneWeightGrams, prev.kundanWeight);

        newData.totalGoldPrice = calculateTotalGoldPrice(
          netWeight,
          field === 'goldRate' ? value : prev.goldRate,
          field === 'purity' ? value : prev.purity
        );
      }

      return newData;
    });
  };

  // Handle save inventory
  const handleSaveInventory = async () => {
    // Validation
    const isSamCategory = inventoryData.itemCode.category === 'SAM';
    if (!inventoryData.itemCode.category || (!isSamCategory && !inventoryData.vendorId)) {
      alert('Please fill in required fields: Category and Vendor');
      return;
    }

    if (inventoryData.grossWeight <= 0) {
      alert('Please enter a valid gross weight');
      return;
    }

    if (!inventoryData.itemCode.number || inventoryData.itemCode.number === 'Generating...' || inventoryData.itemCode.number === 'Error generating code') {
      alert('Please wait for item code generation to complete');
      return;
    }

    try {
      // Find category and vendor
      const category = categories.find(cat => cat.code === inventoryData.itemCode.category);
      const vendor = vendors.find(ven => ven.id === parseInt(inventoryData.vendorId));

      if (!category) {
        alert('Invalid category selected');
        return;
      }

      if (!isSamCategory && !vendor) {
        alert('Invalid vendor selected');
        return;
      }

      // Calculate total stone weight in grams (respecting each stone's unit from DB/state)
      const totalStoneWeightGrams = stonesTotalGrams(inventoryData.stones);

      // Calculate net weight
      const netWeight = calculateGoldNetWeight(inventoryData.grossWeight, totalStoneWeightGrams, inventoryData.kundanWeight);

      // Single cost breakdown — one call, used for both payload and logs
      const costBreakdown = calculateCostBreakdown({ ...inventoryData, netWeight });

      // Prepare inventory data for API
      const inventoryPayload = {
        code: inventoryData.itemCode.number,
        name: inventoryData.jewelryName || inventoryData.itemCode.number,
        description: inventoryData.description,
        category_id: category.id,
        vendor_id: vendor?.id || null,
        gross_weight: inventoryData.grossWeight,
        net_weight: netWeight,
        stone_weight: totalStoneWeightGrams, // Store in grams
        gold_purity: inventoryData.purity,
        gold_rate: inventoryData.goldRate,
        fine_percentage: inventoryData.finePercent ? parseFloat(inventoryData.finePercent) : null,
        kundan_weight: inventoryData.kundanWeight ? parseFloat(inventoryData.kundanWeight) : null,
        kundan_percentage: inventoryData.kundanPct ? parseFloat(inventoryData.kundanPct) : null,
        total_gold_price: inventoryData.totalGoldPrice,
        making_charges: inventoryData.makingCharges || 0,
        labor_charges: inventoryData.laborCharges || 0, // Store per-gram rate
        wastage_percentage: inventoryData.wastagePercentage || 0,
        other_costs: inventoryData.otherCosts || 0,
        total_stone_cost: inventoryData.totalStoneCost || 0,
        stones: inventoryData.stones || [],
        certificate: inventoryData.certificate,
        certificate_rate: inventoryData.certificate === 'Yes' ? (inventoryData.certificateRate || 0) : 0,
        certificate_cost: costBreakdown.certificateCost,
        total_cost_value: costBreakdown.total,
        sale_price: 0, // Can be set later
        status: 'In Stock',
        notes: `Pieces: ${inventoryData.pcs}${inventoryData.stones?.length ? `, Stones: ${inventoryData.stones.length}` : ''}`
      };

      console.log('=== COST BREAKDOWN ===');
      console.log('Gold:', costBreakdown.goldCost, '| Wastage:', costBreakdown.wastageCost, '| Stones:', costBreakdown.stoneCost);
      console.log('Making:', costBreakdown.makingCharges, '| Labor:', costBreakdown.laborCost, '| Certificate:', costBreakdown.certificateCost, '| Other:', costBreakdown.otherCosts);
      console.log('Total:', costBreakdown.total);
      console.log('======================');
      console.log('Saving inventory item with payload:', inventoryPayload);

      // Save to database using API
      const result = await apiService.addInventoryItem(inventoryPayload);

      console.log('Inventory item saved successfully:', result);

      // Extract jewelry data for later use (print tag)
      const jewelry = result && (result.jewelry || result.id) ? (result.jewelry || result) : null;

      console.log('=== SAVED ITEM DEBUG ===');
      console.log('Jewelry object:', jewelry);
      console.log('Result structure:', result);
      console.log('========================');

      // Update local state with the saved item
      if (jewelry) {
        const savedItem = {
          id: jewelry.id,
          code: inventoryPayload.code,
          name: inventoryPayload.name,
          category: category.name,
          grossWeight: inventoryPayload.gross_weight,
          netWeight: inventoryPayload.net_weight,
          purity: inventoryPayload.gold_purity,
          totalGoldPrice: inventoryPayload.total_gold_price,
          totalStoneCost: inventoryPayload.total_stone_cost,
          totalCostValue: inventoryPayload.total_cost_value,
          stones: inventoryPayload.stones,
          status: inventoryPayload.status,
          createdDate: new Date().toISOString()
        };

        console.log('Saved item for display:', savedItem);

        const updatedJewelryPieces = [...(jewelryPieces || []), savedItem];
        setJewelryPieces(updatedJewelryPieces);
      }

      // Reset form
      setInventoryData({
        date: todayCentral(),
        itemCode: { category: '', number: '' },
        vendorId: '',
        jewelryName: '',
        description: '',
        grossWeight: 0,
        netWeight: 0,
        goldRate: currentGoldRate,
        pcs: 1,
        purity: '22KT',
        certificate: 'No',
        certificateRate: 750, // Reset to default rate
        totalGoldPrice: 0,
        makingCharges: 0,
        laborCharges: 0,
        wastagePercentage: 0,
        otherCosts: 0,

        // Reset stone management
        stones: [],
        totalStoneCost: 0,
        totalStoneWeight: 0
      });

      setShowAddForm(false);

      // Refresh the jewelry pieces list
      await loadJewelryPieces();

      // Open label preview modal so user can print the tag immediately
      if (jewelry && jewelry.id) {
        setLabelItemId(jewelry.id);
      }

    } catch (error) {
      console.error('Error saving inventory:', error);
      alert(`Failed to save inventory item: ${error.message}`);
    }
  };

  const sendTagToPrinter = async (id) => {
    try {
      const res = await fetch(`/api/labels/epl/${id}`, { credentials: 'include' });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${id}.epl`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to send tag to printer: ' + e.message);
    }
  };

  return (
    <div className="p-6 pb-12">
      {labelItemId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 text-center">
            <p className="text-gray-800 font-medium mb-4">Item saved. Send its tag to the printer?</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={async () => { const id = labelItemId; setLabelItemId(null); await sendTagToPrinter(id); }}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Yes, print
              </button>
              <button
                onClick={() => setLabelItemId(null)}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-gray-200 hover:bg-gray-300 text-gray-800"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
      <PageIdentifier pageId={SCREEN_IDS?.INVENTORY?.ADD_ITEM || 'INV-002'} pageName="Add Inventory" />
      <h2 className="text-2xl font-bold mb-6">Add New Inventory</h2>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <button
          onClick={() => setShowAddForm(true)}
          disabled={!canCreate}
          className={`w-full p-8 rounded-lg flex flex-col items-center space-y-3 ${canCreate
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          title={!canCreate ? `No permission to add inventory (Level: ${permissionLevel})` : ''}
        >
          <Plus size={48} />
          <span className="text-xl font-semibold">Add New Inventory Item</span>
          <span className="text-sm opacity-90">
            {canCreate ? 'Click to open the inventory creation form' : 'You do not have permission to add inventory'}
          </span>
        </button>
      </div>

      {/* Recent Inventory Items */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4">Recently Added Inventory</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(jewelryPieces || [])
            .filter(j => j.status !== 'Archived')
            .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
            .slice(0, 6)
            .map(jewelry => (
              <div key={jewelry.id} className="bg-white p-4 rounded-lg shadow border">
                <div className="flex items-center justify-between mb-2">
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                    {jewelry.code}
                  </code>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${jewelry.status === 'In Stock' ? 'bg-green-100 text-green-800' :
                      jewelry.status === 'Sold' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                    }`}>
                    {jewelry.status}
                  </span>
                </div>
                <h4 className="font-semibold">{jewelry.name}</h4>
                <p className="text-sm text-gray-600">{jewelry.category}</p>
                <div className="mt-2 space-y-1">
                  <p className="text-sm">
                    <span className="font-medium">Weight:</span> {jewelry.grossWeight}g
                    {jewelry.stones?.length > 0 && (
                      <span className="text-indigo-600 ml-1">
                        (+{jewelry.stones.length} stone{jewelry.stones.length > 1 ? 's' : ''})
                      </span>
                    )}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Purity:</span> {jewelry.purity}
                  </p>
                  {jewelry.stones?.length > 0 && (
                    <p className="text-xs text-indigo-600">
                      <Gem className="inline w-3 h-3 mr-1" />
                      Stones: ₹{(jewelry.totalStoneCost || 0).toFixed(2)}
                    </p>
                  )}
                  <div className="flex justify-between items-center">
                    <p className="text-lg font-bold text-green-600">
                      ₹{jewelry.totalGoldPrice?.toFixed(2) || '0.00'}
                    </p>
                    {jewelry.totalCostValue && (
                      <p className="text-sm text-gray-600">
                        Total: ₹{jewelry.totalCostValue.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
        {(!jewelryPieces || jewelryPieces.length === 0) && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-600">No inventory items added yet</p>
          </div>
        )}
      </div>

      {/* Add Inventory Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-6xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-gray-800">Add New Inventory Item</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Section 1: Basic Information */}
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
                <Calendar className="mr-2" size={20} />
                Section 1: Basic Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="inline mr-1" size={16} />
                    Date *
                  </label>
                  <input
                    type="date"
                    value={inventoryData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Purchase date (editable)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Hash className="inline mr-1" size={16} />
                    Category *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      autoComplete="off"
                      value={categorySearch || inventoryData.itemCode.category || ''}
                      onChange={(e) => {
                        const q = e.target.value.toUpperCase()
                        setCategorySearch(q)
                        setCategoryHighlight(-1)
                        const exactMatch = categories.find(cat => cat.code && cat.code.toUpperCase() === q)
                        if (exactMatch) {
                          handleCategoryChange(exactMatch.code)
                          setCategoryDropdownOpen(false)
                        } else {
                          setCategoryDropdownOpen(q.length > 0)
                        }
                      }}
                      onFocus={() => {
                        const q = (categorySearch || inventoryData.itemCode.category || '').toUpperCase()
                        setCategoryDropdownOpen(q.length > 0)
                        setCategoryHighlight(-1)
                      }}
                      onBlur={() => setTimeout(() => {
                        const q = (categorySearch || '').toUpperCase()
                        const filtered = categories.filter(cat => cat.code && cat.code.toUpperCase().startsWith(q))
                        if (filtered.length === 1 && q.length > 0) {
                          handleCategoryChange(filtered[0].code)
                          setCategorySearch(filtered[0].code)
                        }
                        setCategoryDropdownOpen(false)
                        setCategoryHighlight(-1)
                      }, 150)}
                      onKeyDown={(e) => {
                        if (!categoryDropdownOpen) return
                        const q = (categorySearch || '').toUpperCase()
                        const filtered = categories.filter(cat => cat.code && cat.code.toUpperCase().startsWith(q))
                        if (e.key === 'ArrowDown') {
                          e.preventDefault()
                          setCategoryHighlight(h => Math.min(h + 1, filtered.length - 1))
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault()
                          setCategoryHighlight(h => Math.max(h - 1, 0))
                        } else if (e.key === 'Enter') {
                          e.preventDefault()
                          const idx = categoryHighlight >= 0 ? categoryHighlight : 0
                          if (filtered[idx]) {
                            handleCategoryChange(filtered[idx].code)
                            setCategorySearch(filtered[idx].code)
                            setCategoryDropdownOpen(false)
                            setCategoryHighlight(-1)
                          }
                        } else if (e.key === 'Escape') {
                          setCategoryDropdownOpen(false)
                          setCategoryHighlight(-1)
                        }
                      }}
                      placeholder="Type category code…"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {categoryDropdownOpen && (() => {
                      const q = (categorySearch || '').toUpperCase()
                      const filtered = categories.filter(cat => cat.code && cat.code.toUpperCase().startsWith(q))
                      if (filtered.length === 0) return null
                      return (
                        <ul className="absolute z-50 left-0 right-0 bg-white border border-blue-300 rounded shadow-lg max-h-48 overflow-y-auto text-sm mt-0.5">
                          {filtered.map((cat, idx) => (
                            <li
                              key={cat.id}
                              onMouseDown={() => {
                                handleCategoryChange(cat.code)
                                setCategorySearch(cat.code)
                                setCategoryDropdownOpen(false)
                                setCategoryHighlight(-1)
                              }}
                              className={`px-3 py-1.5 cursor-pointer flex gap-2 ${idx === categoryHighlight ? 'bg-blue-100' : 'hover:bg-blue-50'}`}
                            >
                              <span className="font-medium text-blue-700 w-20 flex-shrink-0">{cat.code}</span>
                              <span className="text-gray-600 truncate">{cat.name}</span>
                            </li>
                          ))}
                        </ul>
                      )
                    })()}</div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Hash className="inline mr-1" size={16} />
                    Item Number (Auto-generated)
                  </label>
                  <input
                    type="text"
                    value={inventoryData.itemCode.number}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                    placeholder="Select category to generate"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format: Category-YY-MM-XXX</p>
                </div>
              </div>
            </div>

            {/* Section 2: Inventory Details */}
            <div className="bg-green-50 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
                <Gem className="mr-2" size={20} />
                Section 2: Inventory Details
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="inline mr-1" size={16} />
                    Vendor {inventoryData.itemCode.category === 'SAM' ? <span className="text-gray-400 font-normal">(Optional)</span> : '*'}
                  </label>
                  <select
                    value={inventoryData.vendorId}
                    onChange={(e) => handleInputChange('vendorId', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.vendor_code ? `${vendor.vendor_code} - ${vendor.name}` : vendor.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Hash className="inline mr-1" size={16} />
                    Pieces
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={inventoryData.pcs}
                    onChange={(e) => handleInputChange('pcs', parseInt(e.target.value) || 1)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Award className="inline mr-1" size={16} />
                    Purity
                  </label>
                  <select
                    value={inventoryData.purity}
                    onChange={(e) => {
                      const newPurity = e.target.value;
                      setInventoryData(prev => ({
                        ...prev,
                        purity: newPurity,
                        totalGoldPrice: calculateTotalGoldPrice(prev.netWeight, prev.goldRate, newPurity)
                      }));
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    {purityOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <CheckCircle className="inline mr-1" size={16} />
                    Certificate
                  </label>
                  <select
                    value={inventoryData.certificate}
                    onChange={(e) => handleInputChange('certificate', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {/* Certificate Rate - Only show when certificate is Yes */}
                {inventoryData.certificate === 'Yes' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <CheckCircle className="inline mr-1" size={16} />
                      Certificate Rate (₹ per carat)
                    </label>
                    <input
                      type="number"
                      value={inventoryData.certificateRate}
                      onChange={(e) => handleInputChange('certificateRate', parseFloat(e.target.value) || 0)}
                      step="0.01"
                      min="0"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Enter rate per carat"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Cost calculated as: Rate × Total Diamond Carats
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="inline mr-1" size={16} />
                  Jewelry Name <span className="text-xs text-gray-400 font-normal">(shown in estimates &amp; billing)</span>
                </label>
                <input
                  type="text"
                  value={inventoryData.jewelryName}
                  onChange={(e) => handleInputChange('jewelryName', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g. Diamond Bangles, Gold Necklace Set..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="inline mr-1" size={16} />
                  Description
                </label>
                <textarea
                  value={inventoryData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Vendor tag information or other details for reference"
                />
              </div>
            </div>

            {/* Weight and Pricing Section */}
            <div className="bg-yellow-50 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center">
                <Scale className="mr-2" size={20} />
                Weight & Pricing Details
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Scale className="inline mr-1" size={16} />
                    Gross Weight (grams) *
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={inventoryData.grossWeight}
                    onChange={(e) => {
                      const newGrossWeight = parseFloat(e.target.value) || 0;
                      setInventoryData(prev => {
                        const stoneWeightGrams = stonesTotalGrams(prev.stones);
                        const newNetWeight = calculateGoldNetWeight(newGrossWeight, stoneWeightGrams, prev.kundanWeight);
                        return {
                          ...prev,
                          grossWeight: newGrossWeight,
                          totalStoneWeight: stoneWeightGrams,
                          netWeight: newNetWeight,
                          totalGoldPrice: calculateTotalGoldPrice(newNetWeight, prev.goldRate, prev.purity)
                        };
                      });
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="0.000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Scale className="inline mr-1" size={16} />
                    Net Weight (grams)
                  </label>
                  <input
                    type="number"
                    value={inventoryData.netWeight.toFixed(3)}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated: Gross Weight − Stone Weight − Kundan Weight (grams). Customers see Fine Net Wt (Gross − Stones).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <DollarSign className="inline mr-1" size={16} />
                    Gold Rate (₹/gram, 24KT)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inventoryData.goldRate}
                    onChange={(e) => {
                      const newGoldRate = parseFloat(e.target.value) || 0;
                      setInventoryData(prev => ({
                        ...prev,
                        goldRate: newGoldRate,
                        totalGoldPrice: calculateTotalGoldPrice(prev.netWeight, newGoldRate, prev.purity)
                      }));
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Current rate from dpgold.com</p>
                </div>

                {/* Fine % (22 FINE %) — can exceed 100, e.g. 104 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    22 Fine %
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={inventoryData.finePercent}
                    onChange={(e) => handleInputChange('finePercent', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="e.g. 104"
                  />
                  <p className="text-xs text-gray-500 mt-1">22K fine % for gold cost (e.g. 104)</p>
                </div>

                {/* Kundan Weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    KUN WT (g)
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={inventoryData.kundanWeight}
                    onChange={(e) => handleInputChange('kundanWeight', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="0.000"
                  />
                  <p className="text-xs text-gray-500 mt-1">Kundan lac/wax weight in grams</p>
                </div>

                {/* Kundan Fine % (K FINE %) — can exceed 100, e.g. 125 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    K Fine %
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={inventoryData.kundanPct}
                    onChange={(e) => handleInputChange('kundanPct', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="e.g. 125"
                  />
                  <p className="text-xs text-gray-500 mt-1">Kundan fine % for gold cost (e.g. 125)</p>
                </div>
              </div>
            </div>

            {/* Stone Management Section */}
            <div className="bg-indigo-50 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-indigo-800 mb-4 flex items-center justify-between">
                <div className="flex items-center">
                  <Gem className="mr-2" size={20} />
                  Stone Management
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newStone = {
                      id: Date.now(),
                      stoneCode: '',
                      stoneName: '',
                      weight: 0,
                      unit: 'gram',
                      costPrice: 0,
                      salePrice: 0
                    };
                    lastAddedStoneId.current = newStone.id;
                    setInventoryData(prev => ({
                      ...prev,
                      stones: [newStone, ...(prev.stones || [])]
                    }));
                  }}
                  className="bg-indigo-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-indigo-700 flex items-center"
                >
                  <Plus size={16} className="mr-1" />
                  Add Stone
                </button>
              </h4>

              {(!inventoryData.stones || inventoryData.stones.length === 0) ? (
                <div className="text-center py-8 bg-white rounded-lg border-2 border-dashed border-indigo-300">
                  <Gem className="mx-auto mb-2 text-indigo-400" size={32} />
                  <p className="text-indigo-600 font-medium">No stones added yet</p>
                  <p className="text-indigo-500 text-sm">Click "Add Stone" to start managing stones</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {inventoryData.stones.map((stone, index) => (
                    <div key={stone.id || index} className="bg-white p-4 rounded-lg border border-indigo-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-center">
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Material Code</label>
                          <input
                            ref={el => { stoneInputRefs.current[stone.id] = el; }}
                            type="text"
                            autoComplete="off"
                            value={stoneSearch[stone.id] !== undefined ? stoneSearch[stone.id] : (stone.stoneCode || '')}
                            onChange={(e) => {
                              const q = e.target.value.toUpperCase();
                              setStoneSearch(prev => ({ ...prev, [stone.id]: q }));
                              setStoneHighlight(prev => ({ ...prev, [stone.id]: -1 }));
                              const exactMatch = materials.find(m => m.code && m.code.toUpperCase() === q);
                              if (exactMatch) {
                                const materialUnit = exactMatch.unit ? ({ 'CARAT': 'carat', 'GRAMS': 'gram', 'NOS': 'number' }[exactMatch.unit.toUpperCase()] || 'gram') : 'gram';
                                const newStones = [...inventoryData.stones];
                                newStones[index] = { ...newStones[index], stoneCode: exactMatch.code, stoneName: exactMatch.name || '', unit: materialUnit, costPrice: exactMatch.cost_price || 0, salePrice: exactMatch.sale_price || 0 };
                                const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                const newNetWeight = calculateGoldNetWeight(inventoryData.grossWeight, totalStoneWeightGrams, inventoryData.kundanWeight);
                                setInventoryData(prev => ({ ...prev, stones: newStones, totalStoneWeight: totalStoneWeightGrams, netWeight: newNetWeight, totalGoldPrice: calculateTotalGoldPrice(newNetWeight, prev.goldRate, prev.purity), totalStoneCost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.costPrice || 0)), 0) }));
                                setStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                              } else {
                                setStoneDropdownOpen(prev => ({ ...prev, [stone.id]: q.length > 0 }));
                              }
                            }}
                            onFocus={() => {
                              const q = stoneSearch[stone.id] !== undefined ? stoneSearch[stone.id] : (stone.stoneCode || '');
                              setStoneDropdownOpen(prev => ({ ...prev, [stone.id]: q.length > 0 }));
                              setStoneHighlight(prev => ({ ...prev, [stone.id]: -1 }));
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                const q = (stoneSearch[stone.id] || '').toUpperCase();
                                const filtered = materials.filter(m => m.code && m.code.toUpperCase().startsWith(q));
                                if (filtered.length === 1 && q.length > 0) {
                                  const material = filtered[0];
                                  const materialUnit = material.unit ? ({ 'CARAT': 'carat', 'GRAMS': 'gram', 'NOS': 'number' }[material.unit.toUpperCase()] || 'gram') : 'gram';
                                  const newStones = [...inventoryData.stones];
                                  newStones[index] = { ...newStones[index], stoneCode: material.code, stoneName: material.name || '', unit: materialUnit, costPrice: material.cost_price || 0, salePrice: material.sale_price || 0 };
                                  const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                  const newNetWeight = calculateGoldNetWeight(inventoryData.grossWeight, totalStoneWeightGrams, inventoryData.kundanWeight);
                                  setInventoryData(prev => ({ ...prev, stones: newStones, totalStoneWeight: totalStoneWeightGrams, netWeight: newNetWeight, totalGoldPrice: calculateTotalGoldPrice(newNetWeight, prev.goldRate, prev.purity), totalStoneCost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.costPrice || 0)), 0) }));
                                  setStoneSearch(prev => ({ ...prev, [stone.id]: material.code }));
                                }
                                setStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                                setStoneHighlight(prev => ({ ...prev, [stone.id]: -1 }));
                              }, 150);
                            }}
                            onKeyDown={(e) => {
                              if (!stoneDropdownOpen[stone.id]) return;
                              const q = (stoneSearch[stone.id] || '').toUpperCase();
                              const filtered = materials.filter(m => m.code && m.code.toUpperCase().startsWith(q));
                              const hi = stoneHighlight[stone.id] !== undefined ? stoneHighlight[stone.id] : -1;
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                setStoneHighlight(prev => ({ ...prev, [stone.id]: Math.min(hi + 1, filtered.length - 1) }));
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                setStoneHighlight(prev => ({ ...prev, [stone.id]: Math.max(hi - 1, 0) }));
                              } else if (e.key === 'Enter') {
                                e.preventDefault();
                                const idx = hi >= 0 ? hi : 0;
                                const material = filtered[idx];
                                if (material) {
                                  const materialUnit = material.unit
                                    ? { 'CARAT': 'carat', 'GRAMS': 'gram', 'NOS': 'number' }[material.unit.toUpperCase()] || 'gram'
                                    : 'gram';
                                  const newStones = [...inventoryData.stones];
                                  newStones[index] = {
                                    ...newStones[index],
                                    stoneCode: material.code,
                                    stoneName: material.name || '',
                                    unit: materialUnit,
                                    costPrice: material.cost_price || 0,
                                    salePrice: material.sale_price || 0
                                  };
                                  const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                  const newNetWeight = calculateGoldNetWeight(inventoryData.grossWeight, totalStoneWeightGrams, inventoryData.kundanWeight);
                                  setInventoryData(prev => ({
                                    ...prev,
                                    stones: newStones,
                                    totalStoneWeight: totalStoneWeightGrams,
                                    netWeight: newNetWeight,
                                    totalGoldPrice: calculateTotalGoldPrice(newNetWeight, prev.goldRate, prev.purity),
                                    totalStoneCost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.costPrice || 0)), 0)
                                  }));
                                  setStoneSearch(prev => ({ ...prev, [stone.id]: material.code }));
                                  setStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                                  setStoneHighlight(prev => ({ ...prev, [stone.id]: -1 }));
                                }
                              } else if (e.key === 'Escape') {
                                setStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                                setStoneHighlight(prev => ({ ...prev, [stone.id]: -1 }));
                              }
                            }}
                            className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Type code…"
                          />
                          {stoneDropdownOpen[stone.id] && (() => {
                            const q = (stoneSearch[stone.id] || '').toUpperCase();
                            const filtered = materials.filter(m => m.code && m.code.toUpperCase().startsWith(q));
                            if (filtered.length === 0) return null;
                            const hi = stoneHighlight[stone.id] !== undefined ? stoneHighlight[stone.id] : -1;
                            return (
                              <ul className="absolute z-50 left-0 right-0 bg-white border border-indigo-300 rounded shadow-lg max-h-48 overflow-y-auto text-sm mt-0.5">
                                {filtered.map((material, idx) => (
                                  <li
                                    key={material.id}
                                    onMouseDown={() => {
                                      const materialUnit = material.unit
                                        ? { 'CARAT': 'carat', 'GRAMS': 'gram', 'NOS': 'number' }[material.unit.toUpperCase()] || 'gram'
                                        : 'gram';
                                      const newStones = [...inventoryData.stones];
                                      newStones[index] = {
                                        ...newStones[index],
                                        stoneCode: material.code,
                                        stoneName: material.name || '',
                                        unit: materialUnit,
                                        costPrice: material.cost_price || 0,
                                        salePrice: material.sale_price || 0
                                      };
                                      const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                      const newNetWeight = calculateGoldNetWeight(inventoryData.grossWeight, totalStoneWeightGrams, inventoryData.kundanWeight);
                                      setInventoryData(prev => ({
                                        ...prev,
                                        stones: newStones,
                                        totalStoneWeight: totalStoneWeightGrams,
                                        netWeight: newNetWeight,
                                        totalGoldPrice: calculateTotalGoldPrice(newNetWeight, prev.goldRate, prev.purity),
                                        totalStoneCost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.costPrice || 0)), 0)
                                      }));
                                      setStoneSearch(prev => ({ ...prev, [stone.id]: material.code }));
                                      setStoneDropdownOpen(prev => ({ ...prev, [stone.id]: false }));
                                      setStoneHighlight(prev => ({ ...prev, [stone.id]: -1 }));
                                    }}
                                    className={`px-3 py-1.5 cursor-pointer flex gap-2 ${idx === hi ? 'bg-indigo-100' : 'hover:bg-indigo-50'}`}
                                  >
                                    <span className="font-medium text-indigo-700 w-20 flex-shrink-0">{material.code}</span>
                                    <span className="text-gray-600 truncate">{material.name}</span>
                                  </li>
                                ))}
                              </ul>
                            );
                          })()}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Material Name</label>
                          <input
                            type="text"
                            value={stone.stoneName || ''}
                            onChange={(e) => {
                              const newStones = [...inventoryData.stones];
                              newStones[index] = { ...newStones[index], stoneName: e.target.value };
                              setInventoryData(prev => ({ ...prev, stones: newStones }));
                            }}
                            className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Material name"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Weight</label>
                          <div className="flex gap-1">
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              value={stone.weight || ''}
                              onChange={(e) => {
                                const newStones = [...inventoryData.stones];
                                newStones[index] = { ...newStones[index], weight: e.target.value };
                                const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                const newNetWeight = calculateGoldNetWeight(inventoryData.grossWeight, totalStoneWeightGrams, inventoryData.kundanWeight);
                                setInventoryData(prev => ({
                                  ...prev,
                                  stones: newStones,
                                  totalStoneWeight: totalStoneWeightGrams,
                                  netWeight: newNetWeight,
                                  totalGoldPrice: calculateTotalGoldPrice(newNetWeight, prev.goldRate, prev.purity),
                                  totalStoneCost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.costPrice || 0)), 0)
                                }));
                              }}
                              className="w-full p-2 text-sm border border-gray-300 rounded-l focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="0.000"
                            />
                            <select
                              value={stone.unit || 'gram'}
                              onChange={(e) => {
                                const newStones = [...inventoryData.stones];
                                newStones[index] = { ...newStones[index], unit: e.target.value };
                                const totalStoneWeightGrams = stonesTotalGrams(newStones);
                                const newNetWeight = calculateGoldNetWeight(inventoryData.grossWeight, totalStoneWeightGrams, inventoryData.kundanWeight);
                                setInventoryData(prev => ({
                                  ...prev,
                                  stones: newStones,
                                  totalStoneWeight: totalStoneWeightGrams,
                                  netWeight: newNetWeight,
                                  totalGoldPrice: calculateTotalGoldPrice(newNetWeight, prev.goldRate, prev.purity),
                                }));
                              }}
                              className="p-2 text-xs border border-l-0 border-gray-300 rounded-r bg-gray-50 focus:ring-1 focus:ring-indigo-500"
                            >
                              <option value="gram">g</option>
                              <option value="carat">ct</option>
                              <option value="number">no</option>
                            </select>
                          </div>
                        </div>

                        {showCostPrice && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Cost Price (₹/unit)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={stone.costPrice || ''}
                            onChange={(e) => {
                              const newStones = [...inventoryData.stones];
                              newStones[index] = { ...newStones[index], costPrice: parseFloat(e.target.value) || 0 };
                              setInventoryData(prev => ({
                                ...prev,
                                stones: newStones,
                                totalStoneCost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.costPrice || 0)), 0)
                              }));
                            }}
                            className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="0.00"
                          />
                        </div>
                        )}

                        {showCostPrice && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Sale Price (₹/unit)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={stone.salePrice || ''}
                            onChange={(e) => {
                              const newStones = [...inventoryData.stones];
                              newStones[index] = { ...newStones[index], salePrice: parseFloat(e.target.value) || 0 };
                              setInventoryData(prev => ({ ...prev, stones: newStones }));
                            }}
                            className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="0.00"
                          />
                        </div>
                        )}

                        <div className="flex items-center justify-between">
                          {showCostPrice && (
                          <div className="text-xs">
                            <div className="font-medium text-gray-700">Total Cost:</div>
                            <div className="text-indigo-600 font-bold">
                              ₹{((parseFloat(stone.weight) || 0) * (stone.costPrice || 0)).toFixed(2)}
                            </div>
                            <div className="text-gray-400 mt-0.5">
                              {stone.unit === 'carat' ? `${stone.weight || 0} ct` : stone.unit === 'number' ? `${stone.weight || 0} pcs` : `${stone.weight || 0} g`}
                            </div>
                          </div>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const newStones = inventoryData.stones.filter((_, i) => i !== index);
                              const totalStoneWeightGrams = stonesTotalGrams(newStones);
                              const newNetWeight = calculateGoldNetWeight(inventoryData.grossWeight, totalStoneWeightGrams, inventoryData.kundanWeight);

                              setInventoryData(prev => ({
                                ...prev,
                                stones: newStones,
                                totalStoneWeight: totalStoneWeightGrams,
                                netWeight: newNetWeight,
                                totalGoldPrice: calculateTotalGoldPrice(newNetWeight, prev.goldRate, prev.purity),
                                totalStoneCost: newStones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.costPrice || 0)), 0)
                              }));
                            }}
                            className="text-red-600 hover:text-red-800 ml-2"
                            title="Remove stone"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Stone Summary */}
                  {(() => {
                    const stones = inventoryData.stones || [];
                    const caratStones = stones.filter(s => s.unit === 'carat');
                    const gramStones = stones.filter(s => s.unit === 'gram');
                    const numberStones = stones.filter(s => s.unit === 'number');
                    const totalCarats = caratStones.reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0);
                    const totalGrams = gramStones.reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0);
                    const totalPcs = numberStones.reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0);
                    const weightParts = [];
                    if (caratStones.length) weightParts.push(`${totalCarats.toFixed(3)} ct`);
                    if (gramStones.length) weightParts.push(`${totalGrams.toFixed(3)} g`);
                    if (numberStones.length) weightParts.push(`${totalPcs} pcs`);
                    const deductedGrams = stonesTotalGrams(stones);
                    const unitBreakdown = [
                      caratStones.length && `${caratStones.length} carat`,
                      gramStones.length && `${gramStones.length} gram`,
                      numberStones.length && `${numberStones.length} number`
                    ].filter(Boolean).join(', ');
                    // Unit-aware total cost: weight × costPrice per stone
                    const totalCost = stones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.costPrice || 0)), 0);
                    const totalSaleValue = stones.reduce((sum, s) => sum + ((parseFloat(s.weight) || 0) * (s.salePrice || 0)), 0);
                    return (
                      <div className="bg-indigo-100 p-3 rounded-lg mt-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="font-medium text-indigo-800">Total Stones:</div>
                            <div className="text-indigo-600 font-bold">{stones.length}</div>
                            {unitBreakdown && <div className="text-indigo-500 text-xs mt-0.5">{unitBreakdown}</div>}
                          </div>
                          <div>
                            <div className="font-medium text-indigo-800">Total Weight:</div>
                            <div className="text-indigo-600 font-bold">
                              {weightParts.length ? weightParts.join(' + ') : '—'}
                            </div>
                            <div className="text-indigo-500 text-xs mt-0.5">
                              {deductedGrams.toFixed(3)} g deducted
                            </div>
                          </div>
                          <div>
                            <div className="font-medium text-indigo-800">Total Cost:</div>
                            <div className="text-indigo-600 font-bold">₹{totalCost.toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="font-medium text-indigo-800">Total Sale Value:</div>
                            <div className="text-indigo-600 font-bold">₹{totalSaleValue.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Additional Costs Section */}
            <div className="bg-orange-50 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-orange-800 mb-4 flex items-center">
                <DollarSign className="mr-2" size={20} />
                Additional Costs
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Award className="inline mr-1" size={16} />
                    Making Charges (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inventoryData.makingCharges}
                    onChange={(e) => handleInputChange('makingCharges', parseFloat(e.target.value) || 0)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Cost for crafting/manufacturing</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="inline mr-1" size={16} />
                    Labor Charges (₹ per gram)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inventoryData.laborCharges}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      const finalValue = isNaN(value) ? 0 : value;
                      console.log('Labor Charges input changed:', e.target.value, '→', finalValue);
                      handleInputChange('laborCharges', finalValue);
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Rate per gram (Total = Rate × Net Weight)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <TrendingUp className="inline mr-1" size={16} />
                    Wastage Percentage (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={inventoryData.wastagePercentage}
                    onChange={(e) => handleInputChange('wastagePercentage', parseFloat(e.target.value) || 0)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Material wastage percentage</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Plus className="inline mr-1" size={16} />
                    Other Costs (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inventoryData.otherCosts}
                    onChange={(e) => handleInputChange('otherCosts', parseFloat(e.target.value) || 0)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Miscellaneous costs</p>
                </div>
              </div>

              {/* Additional Costs Summary */}
              {(() => {
                const cb = calculateCostBreakdown(inventoryData);
                return (
                  <div className="bg-white p-4 rounded-lg border border-orange-200 mt-4">
                    <h5 className="font-medium text-gray-700 mb-2 flex items-center">
                      <Calculator className="mr-1" size={16} />
                      Additional Costs Summary
                    </h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">Making Charges:</div>
                        <div className="font-bold text-orange-600">₹{cb.makingCharges.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Labor Charges:</div>
                        <div className="font-bold text-orange-600">₹{cb.laborCost.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">₹{(inventoryData.laborCharges || 0).toFixed(2)}/g × {inventoryData.netWeight.toFixed(3)}g</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Other Costs:</div>
                        <div className="font-bold text-orange-600">₹{cb.otherCosts.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Total Additional:</div>
                        <div className="font-bold text-orange-700 text-lg">
                          ₹{(cb.makingCharges + cb.laborCost + cb.otherCosts).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Calculation Summary */}
            <div className="bg-purple-50 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-purple-800 mb-4 flex items-center">
                <DollarSign className="mr-2" size={20} />
                Price Calculation
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Gold Cost Calculation */}
                <div className="bg-white p-4 rounded-lg border">
                  <h5 className="font-medium text-gray-700 mb-2 flex items-center">
                    <Award className="mr-1" size={16} />
                    Gold Cost
                  </h5>
                  <p className="text-sm text-gray-600 font-mono mb-2">
                    Net Weight × Gold Rate × Purity Factor
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    {inventoryData.netWeight.toFixed(3)}g × ₹{inventoryData.goldRate} × {getGoldPurityFactor(purityOptions.find(p => p.value === inventoryData.purity)?.numeric || 0).toFixed(4)} ({inventoryData.purity})
                  </p>
                  <p className="text-xl font-bold text-yellow-600">
                    ₹{inventoryData.totalGoldPrice.toFixed(2)}
                  </p>
                </div>

                {/* Stone Cost Calculation */}
                {showCostPrice && (
                <div className="bg-white p-4 rounded-lg border">
                  <h5 className="font-medium text-gray-700 mb-2 flex items-center">
                    <Gem className="mr-1" size={16} />
                    Stone Cost
                  </h5>
                  <p className="text-sm text-gray-600 mb-2">
                    {inventoryData.stones?.length || 0} stone(s)
                  </p>
                  <p className="text-xs text-gray-500 mb-2">
                    {inventoryData.stones?.length > 0
                      ? inventoryData.stones.map((s, i) => {
                        const w = parseFloat(s.weight) || 0;
                        const unit = s.unit || 'gram';
                        const label = unit === 'carat' ? 'ct' : unit === 'number' ? 'pcs' : 'g';
                        return `${w.toFixed(3)}${label}`;
                      }).join(' + ')
                      : 'No stones'}
                  </p>
                  <p className="text-xl font-bold text-indigo-600">
                    ₹{(inventoryData.totalStoneCost || 0).toFixed(2)}
                  </p>
                </div>
                )}

                {/* Total Cost Summary */}
                {(() => {
                  const cb = calculateCostBreakdown(inventoryData);
                  return (
                    <div className="bg-white p-4 rounded-lg border border-green-200">
                      <h5 className="font-medium text-gray-700 mb-2 flex items-center">
                        <DollarSign className="mr-1" size={16} />
                        Total Cost
                      </h5>
                      <div className="space-y-1 text-sm text-gray-600 mb-2">
                        <div className="flex justify-between">
                          <span>Gold:</span>
                          <span>₹{cb.goldCost.toFixed(2)}</span>
                        </div>
                        {cb.wastageCost > 0 && (
                          <div className="flex justify-between text-amber-700">
                            <span>Wastage ({inventoryData.wastagePercentage}%):</span>
                            <span>₹{cb.wastageCost.toFixed(2)}</span>
                          </div>
                        )}
                        {showCostPrice && (
                        <div className="flex justify-between">
                          <span>Stones:</span>
                          <span>₹{cb.stoneCost.toFixed(2)}</span>
                        </div>
                        )}
                        <div className="flex justify-between">
                          <span>Making:</span>
                          <span>₹{cb.makingCharges.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Labor:</span>
                          <span>₹{cb.laborCost.toFixed(2)}</span>
                        </div>
                        {cb.certificateCost > 0 && (
                          <div className="flex justify-between">
                            <span>Certificate ({cb.totalDiamondCarats.toFixed(3)}ct):</span>
                            <span>₹{cb.certificateCost.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span>Other:</span>
                          <span>₹{cb.otherCosts.toFixed(2)}</span>
                        </div>
                        <hr className="border-gray-300" />
                      </div>
                      <p className="text-xl font-bold text-green-600">
                        ₹{cb.total.toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">
                        For {inventoryData.pcs} piece(s)
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-6 border-t border-gray-200">
              <button
                onClick={handleSaveInventory}
                disabled={!inventoryData.itemCode.category || (inventoryData.itemCode.category !== 'SAM' && !inventoryData.vendorId) || inventoryData.grossWeight <= 0 || !inventoryData.itemCode.number || inventoryData.itemCode.number === 'Generating...'}
                className={`px-6 py-3 rounded-lg font-medium flex items-center space-x-2 ${inventoryData.itemCode.category && (inventoryData.itemCode.category === 'SAM' || inventoryData.vendorId) && inventoryData.grossWeight > 0 && inventoryData.itemCode.number && inventoryData.itemCode.number !== 'Generating...'
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                <Save size={20} />
                <span>Save Inventory Item</span>
              </button>

              <button
                onClick={() => setShowAddForm(false)}
                className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 font-medium flex items-center space-x-2"
              >
                <X size={20} />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddInventory;